import { v4 as uuidv4 } from 'uuid';

/**
 * Global evaluation dictionary mapping Material UI / DataGrid logical operators
 * directly into Django REST Framework backend query suffixes.
 */
export const DJANGO_OPERATOR_MAPPING: Record<string, string> = {
  // Text filtering rules
  contains: '__contains',
  icontains: '__icontains',
  equals: '__exact',
  iequals: '__iexact',
  startsWith: '__startswith',
  endsWith: '__endswith',

  // Base structural match fallbacks
  '=': '',
  is: '',

  // Numerical and Date boundary constraints
  '>': '__gt',
  after: '__gt',
  isAfter: '__gt',
  '>=': '__gte',
  onOrAfter: '__gte',
  '<': '__lt',
  before: '__lt',
  isBefore: '__lt',
  '<=': '__lte',
  onOrBefore: '__lte',

  // Multi-selection array lookups
  isAnyOf: '__in',

  // Null state verification rules
  isEmpty: '__isnull',
  isNotEmpty: '__isnull'
};

/**
 * Universal utility transforming a runtime structural filter node into a flat
 * backend-compliant Django query parameter dictionary entry.
 *
 * @param item - An object containing field identifier, comparison operator, and runtime target value.
 * @returns A flat record entry mapping the fully qualified Django query parameter key and its processed value.
 */
export const buildDjangoFilterObject = (item: {
  field: string;
  operator: string;
  value?: any;
}): Record<string, string> => {
  const rawValue = item.value;
  const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

  // Early bypass check for empty nodes unless querying explicitly for null states
  if (!value && !['isEmpty', 'isNotEmpty'].includes(item.operator)) {
    return {};
  }

  const suffix = DJANGO_OPERATOR_MAPPING[item.operator];

  // Return empty if the operator passed is unrecognized
  if (suffix === undefined) {
    return {};
  }

  // Specialized overrides handling explicit boolean flags for Django's __isnull criteria
  if (item.operator === 'isEmpty') {
    return { [`${item.field}__isnull`]: 'true' };
  }
  if (item.operator === 'isNotEmpty') {
    return { [`${item.field}__isnull`]: 'false' };
  }

  return { [`${item.field}${suffix}`]: value };
};

/**
 * Parameter interface structure defining properties required by the compileDjangoQueryParams helper.
 */
interface CompileDjangoQueryParamsInput {
  /** Array of complex criteria objects structured by local DataGrid filter configurations */
  filterModelItems: Array<{ field: string; operator: string; value?: any }>;
  /** Flat flat value record mapped from user selections across the custom toolbar section */
  filterValues: Record<string, any>;
  /** Complete abstract array storing configuration schemas for active custom table input widgets */
  toolbarFilters: any[];
  /** Immutable default fallback query key-value parameters applied automatically on invocation */
  initialParams?: Record<string, any>;
  /** Raw query string targeting unified full-text index definitions in backend services */
  fullTextQuery?: string;
  /** Ordered text parameters directing Django query sort sequences (e.g., '-created_at') */
  orderMacro?: string;
  /** Reactive filter modifiers flowing dynamically from parent or cross-component state anchors */
  outerParams?: any;
  columns: any[];
  defaultWarehouse?: string | number;
  warehouseFilterParam?: string;
}

/**
 * Fuses structured advanced matrix criteria, flat layout toolbar inputs, isolated interval
 * date-ranges, alphanumeric sorting, and parent layout parameter constraints into a singular
 * synchronized key-value dictionary tailored explicitly for Django query string APIs.
 *
 * @param input - Complete runtime state structures extracted from the table layout layer.
 * @returns A flat query object matching API parameters expected by the backend service layer.
 */
export const compileDjangoQueryParams = ({
  filterModelItems,
  filterValues,
  toolbarFilters,
  initialParams,
  fullTextQuery,
  orderMacro,
  outerParams,
  columns,
  defaultWarehouse,
  warehouseFilterParam
}: CompileDjangoQueryParamsInput): Record<string, any> => {
  let searchParams: Record<string, any> = {};

  // Parse and map complex matrix advanced search structures
  filterModelItems.forEach((item) => {
    searchParams = { ...searchParams, ...buildDjangoFilterObject(item) };
  });

  // Parse and map reactive key-value data models from toolbar widgets
  Object.entries(filterValues).forEach(([key, val]) => {
    if (val === '' || val === null || val === undefined) return;

    const filterConfig = toolbarFilters.find((f) => f.id === key);

    // Evaluate structural tuple arrays to branch single records into separate boundary keys
    if (filterConfig?.type === 'dateRange' && Array.isArray(val)) {
      const [start, end] = val;
      if (start) searchParams[`${key}__gte`] = start;
      if (end) searchParams[`${key}__lte`] = end;
    } else {
      // Evaluate custom query engines or bind values raw if no explicit matching mapper is found
      if (filterConfig && 'gridOperator' in filterConfig && filterConfig.gridOperator) {
        const simulatedItem = { field: key, operator: filterConfig.gridOperator, value: val };
        searchParams = { ...searchParams, ...buildDjangoFilterObject(simulatedItem) };
      } else {
        searchParams[key] = val;
      }
    }
  });

  if (defaultWarehouse) {
    const warehouseKey =
      warehouseFilterParam || columns.find((col) => col.field?.includes('warehouse'))?.field;
    if (warehouseKey) {
      searchParams = { ...searchParams, [warehouseKey]: defaultWarehouse };
    }
  }

  // Synthesize global system pagination page state and indexing sorting constraints
  if (initialParams) searchParams = { ...searchParams, ...initialParams };
  if (fullTextQuery) searchParams['q'] = fullTextQuery;
  if (orderMacro && orderMacro.trim().length > 0) searchParams['ordering'] = orderMacro;
  if (outerParams) searchParams = { ...searchParams, ...outerParams };

  return searchParams;
};

/**
 * Normalizes item layouts returned directly from service endpoints to guarantee that every row
 * possesses a uniform, uniquely defined lookup primary identifier tracking key 'id'. Resolves
 * 'row_id' mappings or safely injects a newly generated uuidv4 block if missing.
 *
 * @param rawResults - Array of unstructured entity rows flowing from backend services.
 * @returns Strongly identified data record collection safe for react virtual list keys.
 */
export const normalizeTableItems = (rawResults: any[]): any[] => {
  return rawResults.map((item: any) => {
    if (item.row_id != null) return { ...item, id: item.row_id };
    if (!item?.id) return { ...item, id: uuidv4() };
    return item;
  });
};

// --------------------------------------------------------------------------------------------

interface CompileDataGridQueryParamsInput {
  filterModelItems: any[];
  initialParams?: Record<string, any>;
  initialFilterItems?: any[];
  skip: boolean;
  defaultWarehouse?: string | number;
  columns: any[];
  fullTextQuery?: string;
  orderMacro?: string;
  filter?: Record<string, any>;
  outerParams?: any;
  warehouseFilterParam?: string;
}

/**
 * Advanced query compiler specifically tailored for the system's DataGrid architecture.
 * Safely handles one-time initial parameters, dynamic warehouse resolution, and query token synthesis.
 */
export const compileDataGridQueryParams = ({
  filterModelItems,
  initialParams,
  initialFilterItems,
  skip,
  defaultWarehouse,
  columns,
  fullTextQuery,
  orderMacro,
  filter,
  outerParams,
  warehouseFilterParam
}: CompileDataGridQueryParamsInput): { searchParams: Record<string, any>; nextSkip: boolean } => {
  let searchParams: Record<string, any> = {};
  let nextSkip = skip;

  // 1. Process standard reactive matrix items
  filterModelItems.forEach((element) => {
    searchParams = { ...searchParams, ...buildDjangoFilterObject(element) };
  });

  // 2. Process one-time initial parameters
  if (!skip && initialParams) {
    searchParams = initialParams;
    nextSkip = true;
  }

  // 3. Process one-time initial advanced filters
  if (!skip && initialFilterItems) {
    let filters = {};
    initialFilterItems.forEach((element) => {
      filters = { ...filters, ...buildDjangoFilterObject(element) };
    });
    searchParams = { ...searchParams, ...filters };
  }

  // 4. Dynamic contextual column matching for warehouses
  if (defaultWarehouse) {
    const warehouseKey =
      warehouseFilterParam || columns.find((col) => col.field?.includes('warehouse'))?.field;
    if (warehouseKey) {
      searchParams = { ...searchParams, [warehouseKey]: defaultWarehouse };
    }
  }

  // 5. Apply universal formatting tokens
  if (fullTextQuery) searchParams['q'] = fullTextQuery;
  if (orderMacro && orderMacro.trim().length !== 0) searchParams['ordering'] = orderMacro;
  if (filter) searchParams = { ...searchParams, ...filter };
  if (outerParams) searchParams = { ...searchParams, ...outerParams };

  return { searchParams, nextSkip };
};
